import { createFileRoute, Link, useParams, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useApp } from "@/store/app";
import {
  useLeagueByCode,
  useLeagueLeaderboard,
  useDeleteLeague,
  useLeaveLeague,
  useUpdateLeague,
} from "@/hooks/useLeaguesDb";

import { BadgeRow } from "@/components/BadgeRow";
import { UserAvatar } from "@/components/UserAvatar";
import { ALL_BADGES } from "@/lib/badges";
import { useUsersBadges } from "@/hooks/useUserBadges";
import { useAdminIds } from "@/hooks/useAdminIds";
import {
  Copy,
  Trash2,
  Trophy,
  Share2,
  X,
  Pencil,
  ImagePlus,
  LogOut,
  Loader2,
  Crown,
  Star,
  Flame,
} from "lucide-react";
import { BackButton } from "@/components/BackButton";
import { compressImage, MAX_ORIGINAL_BYTES } from "@/lib/imageCompression";
import { toast } from "sonner";
import { useT } from "@/i18n";
import logo from "@/assets/logo.png";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_app/leagues/$code")({
  component: LeaguePage,
});

function LeaguePage() {
  const { code } = useParams({ from: "/_app/leagues/$code" });
  const t = useT();
  const user = useApp((s) => s.user);
  const navigate = useNavigate();

  const { data: league, isLoading } = useLeagueByCode(code);
  const { data: board = [] } = useLeagueLeaderboard(league?.id);
  const deleteLeague = useDeleteLeague();
  const leaveLeague = useLeaveLeague();
  const updateLeague = useUpdateLeague();

  const [showShare, setShowShare] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  // IMPORTANT: all hooks must run on every render in the same order. Do not
  // place hooks after an early return below — it breaks the Rules of Hooks
  // and crashes the route boundary on the first load.
  const memberIds = board.map((m) => m.user_id);
  const { data: badgesByUser = {} } = useUsersBadges(memberIds);
  const adminIds = useAdminIds(memberIds);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!league || !user) {
    return (
      <div className="rounded-2xl border border-dashed py-16 text-center">
        <Trophy className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-3 font-semibold">{t("League not found", "Liga não encontrada")}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("This league code doesn't exist.", "Acest cod de ligă nu există.")}
        </p>
        <div className="mt-4 flex justify-center">
          <BackButton to="/leagues" label={t("Leagues", "Ligas", "Ligi")} />
        </div>
      </div>
    );
  }

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/join/${league.code}`
      : `/join/${league.code}`;

  function copyCode() {
    if (!league) return;
    navigator.clipboard.writeText(league.code);
    toast.success(t("Code copied", "Código copiado"));
  }

  const isCreator = league.created_by === user.id && !league.is_protected;
  const isProtected = league.is_protected;

  const decoratedBoard = board.map((m) => {
    const ids = badgesByUser[m.user_id] ?? [];
    return {
      ...m,
      isYou: m.user_id === user.id,
      badges: ALL_BADGES.filter((b) => ids.includes(b.id)),
    };
  });
  

  return (
    <div>
      <div className="mb-4">
        <BackButton to="/leagues" label={t("Leagues", "Ligas", "Ligi")} />
      </div>

      {/* Header */}
      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3">
            <div className="h-[60px] w-[60px] flex-shrink-0">
              {isProtected ? (
                <img src={logo} alt="" className="h-full w-full object-contain" />
              ) : league.image_url ? (
                <img
                  src={league.image_url}
                  alt=""
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    const img = e.currentTarget;
                    img.style.display = "none";
                    const fallback = img.nextElementSibling as HTMLElement | null;
                    if (fallback) fallback.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className="flex h-full w-full items-center justify-center text-muted-foreground"
                style={{ display: isProtected || league.image_url ? "none" : "flex" }}
              >
                <Trophy className="h-6 w-6" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="break-words text-xl font-bold leading-tight tracking-tight text-foreground sm:text-2xl">
                {league.name}
              </h1>
              <div className="mt-1 truncate text-xs text-muted-foreground">
                {league.display_creator_as && (
                  <>
                    {t("Created by", "Criado por")} {league.display_creator_as} ·{" "}
                  </>
                )}
                {board.length} {t("members", "membros")}
              </div>
            </div>
            {isCreator && (
              <button
                onClick={() => setShowEdit(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={t("Edit", "Editar")}
                title={t("Edit", "Editar")}
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
          {league.description && (
            <p className="mt-3 text-sm text-muted-foreground">{league.description}</p>
          )}
        </div>

        {!isProtected && (
          <details className="group border-t border-border/60 bg-background/30">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground sm:px-5">
              <span className="inline-flex items-center gap-2">
                <Share2 className="h-3.5 w-3.5" />
                {t("Share & invite", "Partajează")}
              </span>
              <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground/70 group-open:hidden">
                {league.code}
              </span>
            </summary>
            <div className="flex items-center justify-between gap-2 px-4 pb-3 sm:px-5">
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("League code", "Código da liga")}
                </div>
                <div className="mt-0.5 truncate font-mono text-base font-black tracking-[0.3em] text-premium">
                  {league.code}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={copyCode}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label={t("Copy code", "Copiar código")}
                  title={t("Copy code", "Copiar código")}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setShowShare(true)}
                  className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-premium/40 bg-premium/10 px-3 text-xs font-semibold text-premium hover:bg-premium/20"
                  aria-label={t("Share league", "Compartilhar liga")}
                >
                  <Share2 className="h-3.5 w-3.5" />
                  {t("Share", "Compartilhar")}
                </button>
              </div>
            </div>
          </details>
        )}
      </div>

      {/* Leaderboard */}
      <h2 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("Leaderboard", "Classificação")}
      </h2>
      <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
        {(() => {
          const boardRanks: number[] = [];
          for (let i = 0; i < decoratedBoard.length; i++) {
            boardRanks.push(
              i === 0 || decoratedBoard[i - 1].points !== decoratedBoard[i].points
                ? i + 1
                : boardRanks[i - 1]
            );
          }
          return decoratedBoard.map((m, i) => {
          const rank = boardRanks[i];
          const isTop3 = rank <= 3;
          return (
            <Link
              key={m.user_id}
              to="/players/$id"
              params={{ id: m.user_id }}
              aria-label={t(`View ${m.username}'s profile`, `Ver perfil de ${m.username}`)}
              className={`relative flex min-h-[56px] items-center gap-3 border-b border-border/40 px-3 py-2.5 last:border-b-0 transition-colors hover:bg-accent/30 focus:outline-none focus:ring-2 focus:ring-primary ${
                m.isYou ? "bg-premium/[0.06]" : ""
              }`}
            >
              {m.isYou && (
                <span
                  aria-hidden
                  className="absolute inset-y-0 left-0 w-[3px] bg-premium"
                />
              )}
              <RankBadge rank={rank} />
              <div className={`flex-shrink-0 rounded-full ${m.isYou ? "ring-2 ring-premium ring-offset-2 ring-offset-card" : ""}`}>
                <UserAvatar avatar={m.avatar} username={m.username} size={36} isAdmin={adminIds.has(m.user_id)} />
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-1.5">
                <span
                  className="min-w-0 max-w-[calc(100vw-200px)] truncate text-sm font-medium text-foreground sm:max-w-none"
                  title={m.username}
                >
                  {m.username}
                </span>
                {m.isYou && (
                  <span className="flex-shrink-0 rounded-full border border-premium/60 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-premium">
                    {t("You", "Você")}
                  </span>
                )}
                <div className="flex-shrink-0">
                  <BadgeRow badges={m.badges} username={m.username} />
                </div>
              </div>
              <div className="flex-shrink-0 text-right">
                <div
                  className={`text-base font-black leading-none tabular-nums ${
                    isTop3 ? "text-premium" : "text-muted-foreground"
                  }`}
                >
                  {m.points}
                </div>
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground/70">
                  {t("pts", "pts")}
                </div>
              </div>
            </Link>
          );
        });
        })()}
        {decoratedBoard.length === 0 && (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t("No members yet", "Nenhum membro ainda")}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="mt-8 flex justify-center">
        {isCreator ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="inline-flex items-center gap-2 rounded-lg bg-destructive px-4 py-2.5 text-sm font-semibold text-destructive-foreground shadow-sm hover:opacity-90">
                <Trash2 className="h-4 w-4" />
                {t("Delete League", "Excluir Liga")}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t(`Delete "${league.name}"?`, `Excluir "${league.name}"?`)}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("This permanently removes the league and its leaderboard.", "Aceasta elimină definitiv liga și clasamentul.")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Cancel", "Cancelar")}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    deleteLeague.mutate(league.id, {
                      onSuccess: () => {
                        toast.success(t(`Deleted ${league.name}`, `${league.name} excluída`));
                        navigate({ to: "/leagues" });
                      },
                      onError: (e: Error) =>
                        toast.error(e.message || t("Delete failed", "Eliminare eșuată")),
                    });
                  }}
                >
                  {t("Delete league", "Excluir liga")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          !isProtected && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button className="inline-flex items-center gap-2 rounded-lg border border-destructive/40 px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/10">
                  <LogOut className="h-4 w-4" />
                  {t("Leave league", "Părăsește liga")}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t(`Leave ${league.name}?`, `Părăsești ${league.name}?`)}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("You can rejoin later with the league code.", "Te poți alătura din nou cu codul ligii.")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("Cancel", "Cancelar")}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      leaveLeague.mutate(
                        { userId: user.id, leagueId: league.id },
                        {
                          onSuccess: () => {
                            toast.success(t("Left league", "Ai părăsit liga"));
                            navigate({ to: "/leagues" });
                          },
                          onError: (e: Error) =>
                            toast.error(e.message || t("Could not leave", "Nu am putut părăsi")),
                        }
                      );
                    }}
                  >
                    {t("Leave", "Părăsește")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        )}
      </div>

      {showShare && (
        <ShareDialog
          leagueName={league.name}
          code={league.code}
          url={shareUrl}
          onClose={() => setShowShare(false)}
        />
      )}
      {showEdit && isCreator && (
        <EditLeagueDialog
          initialName={league.name}
          initialDescription={league.description ?? ""}
          initialImage={league.image_url ?? undefined}
          saving={updateLeague.isPending}
          onClose={() => setShowEdit(false)}
          onSave={(patch) => {
            updateLeague.mutate(
              {
                leagueId: league.id,
                name: patch.name,
                description: patch.description,
                imageUrl: patch.image ?? null,
              },
              {
                onSuccess: () => {
                  toast.success(t("League updated", "Liga atualizada"));
                  setShowEdit(false);
                },
                onError: (e: Error) =>
                  toast.error(e.message || t("Update failed", "Actualizare eșuată")),
              }
            );
          }}
        />
      )}
    </div>
  );
}

function ShareDialog({
  leagueName,
  code,
  url,
  onClose,
}: {
  leagueName: string;
  code: string;
  url: string;
  onClose: () => void;
}) {
  const t = useT();
  function copyLink() {
    navigator.clipboard.writeText(url);
    toast.success(
      t(
        "Link copied! Send it to your friends.",
        undefined,
        "Link copiat! Trimite-l prietenilor tăi.",
      ),
    );
  }
  async function handleShare() {
    const shareData = {
      title: "Predictor Pro",
      text: t(
        `Join my Predictor Pro league "${leagueName}"! Use code ${code} or tap the link:`,
        undefined,
        `Alătură-te ligii mele "${leagueName}" pe Predictor Pro! Folosește codul ${code} sau apasă linkul:`,
      ),
      url,
    };
    const nav = navigator as Navigator & {
      canShare?: (d: ShareData) => boolean;
      share?: (d: ShareData) => Promise<void>;
    };
    if (nav.share && (!nav.canShare || nav.canShare(shareData))) {
      try {
        await nav.share(shareData);
      } catch (err) {
        if ((err as { name?: string })?.name !== "AbortError") {
          console.error(err);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success(
          t(
            "Link copied! Send it to your friends.",
            undefined,
            "Link copiat! Trimite-l prietenilor tăi.",
          ),
        );
      } catch {
        toast.error(t("Copy failed", undefined, "Copiere eșuată"));
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-pop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{t("Share league", "Compartilhar liga")}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-accent" aria-label={t("Close", "Fechar")}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-col items-center gap-3 rounded-xl border bg-background p-4">
          <div className="rounded-lg bg-white p-3">
            <QRCodeSVG value={url} size={180} level="M" />
          </div>
          <div className="text-center text-xs text-muted-foreground">
            {t(`Scan to join ${leagueName}`, `Escaneie para entrar em ${leagueName}`)}
          </div>
          <div className="flex flex-col items-center gap-1">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("League code", "Código da liga")}
            </div>
            <div className="font-mono text-2xl font-black tracking-[0.3em] text-primary">
              {code}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("Invite link", "Link de convite")}
          </label>
          <div className="mt-1 flex items-center gap-2 rounded-lg border bg-background px-3 py-2">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 truncate bg-transparent text-sm focus:outline-none"
            />
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-2.5 py-1 text-xs font-semibold hover:bg-accent"
            >
              <Copy className="h-3.5 w-3.5" />
              {t("Copy", "Copiar")}
            </button>
          </div>
        </div>

        <button
          onClick={handleShare}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:opacity-90"
        >
          <Share2 className="h-4 w-4" />
          {t("Share", undefined, "Distribuie")}
        </button>
      </div>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 via-yellow-300 to-amber-500 shadow-[0_0_16px_rgba(251,191,36,0.75)] ring-1 ring-amber-300 animate-pulse"
        aria-label="Rank 1"
      >
        <Crown className="h-4 w-4 text-amber-900" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-slate-300 via-white/70 to-slate-400 shadow-[0_0_10px_rgba(148,163,184,0.45)] ring-1 ring-slate-300/80"
        aria-label="Rank 2"
      >
        <Star className="h-4 w-4 fill-slate-600 text-slate-600" />
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-600 via-amber-600 to-orange-800 shadow-[0_0_10px_rgba(180,83,9,0.45)] ring-1 ring-orange-600/60"
        aria-label="Rank 3"
      >
        <Flame className="h-4 w-4 text-orange-100" />
      </div>
    );
  }
  return (
    <div
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center text-sm font-semibold tabular-nums text-muted-foreground"
      aria-label={`Rank ${rank}`}
    >
      {rank}
    </div>
  );
}

function EditLeagueDialog({
  initialName,
  initialDescription,
  initialImage,
  onClose,
  onSave,
  saving,
}: {
  initialName: string;
  initialDescription: string;
  initialImage?: string;
  saving?: boolean;
  onClose: () => void;
  onSave: (patch: { name: string; description: string; image?: string }) => void;
}) {
  const t = useT();
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [image, setImage] = useState<string | undefined>(initialImage);
  const [compressing, setCompressing] = useState(false);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_ORIGINAL_BYTES) {
      toast.error(t("Image must be under 10MB", "Imaginea trebuie să fie sub 10MB"));
      return;
    }
    setCompressing(true);
    try {
      const dataUrl = await compressImage(file, { maxWidth: 800, maxHeight: 600, quality: 0.85 });
      setImage(dataUrl);
    } catch {
      toast.error(t("Could not process image", "Nu am putut procesa imaginea"));
    } finally {
      setCompressing(false);
      e.target.value = "";
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-pop"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border bg-card p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold">{t("Edit league", "Editar liga")}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-accent" aria-label={t("Close", "Fechar")}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("League image", "Imagem da liga")}
            </label>
            <div className="mt-1 flex items-center gap-3">
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border bg-muted">
                {image ? (
                  <img src={image} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <ImagePlus className="h-6 w-6" />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-input bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent ${compressing ? "pointer-events-none opacity-60" : ""}`}>
                  {compressing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                  {compressing ? t("Processing…", "Se procesează…") : t("Upload image", "Enviar imagem")}
                  <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={compressing} />
                </label>
                {image && (
                  <button
                    type="button"
                    onClick={() => setImage(undefined)}
                    className="text-left text-xs text-muted-foreground hover:text-destructive"
                  >
                    {t("Remove image", "Remover imagem")}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("Name", "Nome")}
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t("Description", "Descrição")}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={280}
              className="mt-1 w-full rounded-lg border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <button
            disabled={saving}
            onClick={() => onSave({ name: name.trim() || initialName, description: description.trim(), image })}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-40"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("Save changes", "Salvar alterações")}
          </button>
        </div>
      </div>
    </div>
  );
}
